import React,{useState} from 'react';
import {useNavigate} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import BusinessSearch from '../components/reviews/BusinessSearch';
import BusinessMap from '../components/reviews/BusinessMap';
import BusinessDirectory from '../components/reviews/BusinessDirectory';
import {createReviewLink} from '../services/reviewLinkService';

const {FiArrowUpRight,FiMapPin,FiSearch,FiLink,FiUsers}=FiIcons;

function WorkspaceIntro() {
  return (
    <div className="relative overflow-hidden rounded-[1.5rem] border border-white/10 bg-[#19283d] px-5 py-8 sm:rounded-[2rem] sm:px-10 sm:py-14">
      <div className="pointer-events-none absolute -right-20 -top-28 h-80 w-80 rounded-full bg-[#3fc0fa]/10 blur-3xl" />
      <div className="relative max-w-3xl">
        <span className="inline-flex items-center gap-2 rounded-full border border-[#3fc0fa]/30 bg-[#3fc0fa]/10 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.14em] text-[#3fc0fa] sm:px-4 sm:text-xs sm:tracking-[0.18em]">
          <SafeIcon icon={FiUsers} /> Jelly Local partners
        </span>
        <h1 className="mt-6 text-[2.25rem] font-black leading-[1.1] tracking-tight sm:mt-7 sm:text-6xl">
          Free Google review links <span className="text-[#3fc0fa]">for local businesses.</span>
        </h1>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300 sm:mt-6 sm:text-lg">
          Find a business,create its link,and share it with the owner.
        </p>
      </div>
    </div>
  );
}

function SelectedBusiness({business,saving,error,onCreate}) {
  return (
    <section aria-live="polite" className="mt-5 min-w-0 overflow-hidden rounded-[1.5rem] border border-[#3fc0fa]/40 bg-[#19283d] sm:rounded-[1.75rem]">
      <div className="p-5 sm:p-8">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#a7e2be]">Selected business</p>
        <h3 className="mt-3 break-words text-2xl font-black sm:text-3xl">{business.name}</h3>
        <p className="mt-3 flex items-start gap-2 text-sm text-slate-300">
          <SafeIcon icon={FiMapPin} className="mt-0.5 shrink-0 text-[#f8b86a]" /> {business.address}
        </p>
        <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <button type="button" disabled={saving} onClick={onCreate} className="site-button inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-center font-bold disabled:opacity-60 sm:w-auto sm:px-6 sm:py-4">
            {saving ? 'Creating link…' : 'Generate free review link'} <SafeIcon icon={FiArrowUpRight} />
          </button>
          {business.mapsUrl && (
            <a href={business.mapsUrl} target="_blank" rel="noopener noreferrer" className="site-outline inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full px-5 py-3 font-semibold sm:w-auto sm:px-6 sm:py-4">
              View listing <SafeIcon icon={FiArrowUpRight} />
            </a>
          )}
        </div>
        {error && <p role="alert" className="mt-4 text-sm text-[#f8b86a]">{error}</p>}
      </div>
      <BusinessMap business={business} />
    </section>
  );
}

export default function ReviewsPage() {
  const [business,setBusiness]=useState(null);
  const [saving,setSaving]=useState(false);
  const [error,setError]=useState('');
  const navigate=useNavigate();
  async function handleCreate() {
    if (!business || saving) return;
    setSaving(true);
    setError('');
    try {
      const record=await createReviewLink(business);
      navigate(`/${record.slug}`);
    } catch {
      setError('Could not create the link. Please try again.');
    } finally {
      setSaving(false);
    }
  }
  return (
    <main className="min-h-screen min-w-0 bg-[#0b1423] pb-20 pt-24 text-white sm:pb-24 sm:pt-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <WorkspaceIntro />
        <div className="mt-7 grid min-w-0 gap-6 sm:mt-12 sm:gap-8 lg:grid-cols-[minmax(0,1fr)_280px] lg:gap-10">
          <div className="min-w-0">
            <section className="min-w-0 rounded-[1.5rem] border border-white/10 bg-[#19283d] p-5 sm:rounded-[1.75rem] sm:p-8" aria-labelledby="find-business-title">
              <div className="mb-5 flex items-start gap-4 sm:mb-6">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#3fc0fa] text-[#0b1423]">
                  <SafeIcon icon={FiSearch} className="h-6 w-6" />
                </span>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#3fc0fa]">Step 01</p>
                  <h2 id="find-business-title" className="mt-2 text-2xl font-black">Find a business</h2>
                </div>
              </div>
              <p className="mb-5 text-sm leading-relaxed text-slate-300">Search by name and location,then choose its Google listing.</p>
              <BusinessSearch onSelect={(selected)=> {setBusiness(selected);setError('');}} />
            </section>
            {business && <SelectedBusiness business={business} saving={saving} error={error} onCreate={handleCreate} />}
          </div>
          <aside className="h-fit rounded-[1.5rem] border border-[#a7e2be]/25 bg-[#a7e2be]/10 p-5 sm:rounded-[1.75rem] sm:p-6">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#a7e2be] text-[#0b1423]">
              <SafeIcon icon={FiLink} className="h-5 w-5" />
            </span>
            <h2 className="mt-5 text-xl font-black">Create. Share. Repeat.</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-300">Each link gives a business its own review page. You can find and edit created pages below.</p>
          </aside>
        </div>
        <BusinessDirectory />
      </div>
    </main>
  );
}